/**
 * The builder's model choices (plan E10-T07): a tenant's model endpoint as a role's provider, a
 * role's route (its fallbacks, in order or by residency), and what a minute of the agent costs.
 */
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useId } from "react";
import { data, type Schemas } from "../../api/client";
import { useApiQuery } from "../../app/Page";
import { useBff, useMe } from "../../app/session";
import { useT, type MessageId } from "../../i18n";
import { NumberField, type Choice } from "./fields";
import { SpecDocument, type Path } from "./specDocument";
import type { Builder } from "./useBuilder";
import { useSettled } from "./useValidation";

type Role = "stt" | "llm" | "tts";
type Ref = { provider?: string; endpoint?: string };

/** The tenant's model endpoints of one kind, for a picker. */
export function useEndpoints(kind: string): Choice[] {
  const endpoints = useApiQuery("model-endpoints", (api) => api.GET("/v1/model-endpoints", { params: { query: { limit: 200 } } }));
  return (endpoints.data?.data ?? [])
    .filter((e: Schemas["ModelEndpointOut"]) => e.kind === kind)
    .map((e: Schemas["ModelEndpointOut"]) => ({ value: e.name, label: e.region ? `${e.name} (${e.region})` : e.name }));
}

/** `provider: endpoint`: which of the tenant's endpoints, in place of a model and a credential. */
export function EndpointPicker({ builder, base, kind }: { builder: Builder; base: Path; kind: Role }) {
  const t = useT();
  const id = useId();
  const choices = useEndpoints(kind);
  const value = String(builder.doc.get([...base, "endpoint"]) ?? "");
  return (
    <div className="space-y-1 sm:col-span-2">
      <label htmlFor={id} className="block text-sm font-medium">{t("builder.endpoint")}</label>
      <select id={id} value={value} disabled={builder.problems.length > 0} data-path={[...base, "endpoint"].join(".")}
        aria-describedby={`${id}-hint`} onChange={(e) => builder.setPath([...base, "endpoint"], e.target.value)}
        className="w-full rounded-md border border-[var(--color-border)] bg-transparent px-3 py-1.5 disabled:opacity-60">
        {!value && <option value="" />}
        {value && !choices.some((c) => c.value === value) && <option value={value}>{value}</option>}
        {choices.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
      </select>
      <p id={`${id}-hint`} className="text-xs text-[var(--color-muted)]">
        {choices.length ? t("builder.endpointHint") : t("builder.noEndpoints", { kind })}
      </p>
    </div>
  );
}

/** Choosing a provider: an endpoint is named, any other provider loses the endpoint. */
export function endpointOnPick(builder: Builder, base: Path, name: string, first: string | undefined) {
  builder.setPath([...base, "endpoint"], name === "endpoint" ? first : undefined);
}

const HINTS: Record<string, MessageId> = {
  residency: "builder.route.residencyHint",
  latency: "builder.route.latencyHint",
  cost: "builder.route.costHint",
  weighted: "builder.route.weightedHint",
};

/** A role's alternatives: its fallback list, or its route (in order, by the data's region, by latency, by cost or
 * weighted: V1-E15). */
export function Routing({ builder, role }: { builder: Builder; role: Role }) {
  const t = useT();
  const id = useId();
  const base: Path = ["spec", role];
  const route = builder.doc.get([...base, "route"]) as
    | { strategy?: string; targets?: Ref[]; weights?: number[] }
    | undefined;
  const fallback = (builder.doc.get([...base, "fallback"]) as Ref[] | undefined) ?? [];
  const targets = route ? route.targets ?? [] : fallback;
  const strategy = route ? route.strategy ?? "fallback" : "";
  const change = (next: string) => {
    if (next === "") {  // back to a plain fallback list
      builder.setPath([...base, "route"], undefined);
      builder.setPath([...base, "fallback"], targets.length ? targets : undefined);
    } else {
      builder.setPath([...base, "fallback"], undefined);
      // weighted (V1-E15): one weight per target, the role's own first, even to begin with
      const weights = next === "weighted" ? (route?.weights ?? Array<number>(targets.length + 1).fill(1)) : undefined;
      builder.setPath([...base, "route"], { ...(route ?? {}), strategy: next, targets, weights });
    }
  };
  const named = (r: Ref) => (r.endpoint ? `endpoint ${r.endpoint}` : r.provider ?? "?");
  const names = targets.map(named);
  const own = named({ provider: builder.doc.get([...base, "provider"]) as string | undefined,
    endpoint: builder.doc.get([...base, "endpoint"]) as string | undefined });
  return (
    <fieldset className="space-y-2 rounded-md border border-[var(--color-border)] p-3">
      <legend className="px-1 text-sm font-medium">{t("builder.routing", { role: role.toUpperCase() })}</legend>
      <div className="space-y-1">
        <label htmlFor={id} className="block text-sm">{t("builder.routeStrategy")}</label>
        <select id={id} value={strategy} disabled={builder.problems.length > 0} onChange={(e) => change(e.target.value)}
          className="rounded-md border border-[var(--color-border)] bg-transparent px-3 py-1.5 disabled:opacity-60">
          <option value="">{t("builder.route.list")}</option>
          <option value="fallback">{t("builder.route.fallback")}</option>
          <option value="residency">{t("builder.route.residency")}</option>
          <option value="latency">{t("builder.route.latency")}</option>
          <option value="cost">{t("builder.route.cost")}</option>
          <option value="weighted">{t("builder.route.weighted")}</option>
        </select>
        <p className="text-xs text-[var(--color-muted)]">{t(HINTS[strategy] ?? "builder.route.fallbackHint")}</p>
      </div>
      <p className="text-sm text-[var(--color-muted)]">
        {names.length ? t("builder.routeTargets", { list: names.join(", ") }) : t("builder.noFallbacks")}
      </p>
      {strategy === "weighted" && (
        <div className="flex flex-wrap gap-3">
          {[own, ...names].map((name, i) => (
            <label key={i} className="space-y-1 text-sm">
              <span className="block">{t("builder.route.weight", { name })}</span>
              <input type="number" inputMode="numeric" min={0} max={1000} step={1}
                value={route?.weights?.[i] ?? 0} disabled={builder.problems.length > 0}
                className="max-w-28 rounded-md border border-[var(--color-border)] bg-transparent px-3 py-1.5"
                onChange={(e) => {  // the whole list: emptying one is 0, never a missing weight
                  const weights = [...(route?.weights ?? [])];
                  weights[i] = Math.max(0, Math.round(Number(e.target.value) || 0));
                  builder.setPath([...base, "route", "weights"], weights);
                }} />
            </label>
          ))}
        </div>
      )}
      {route && role !== "stt" && (
        <NumberField builder={builder} path={[...base, "route", "health", "first_byte_timeout_s"]} label={t("builder.firstByte")}
          hint={t("builder.firstByteHint", { seconds: role === "llm" ? 5 : 3 })} min={0.5} max={30} step={0.5} />
      )}
    </fieldset>
  );
}

const usd = (amount: number) => `$${amount < 0.01 ? amount.toFixed(4) : amount.toFixed(3)}`;

/** What a minute of the agent is estimated to cost, from the price tables and the endpoints' prices. */
export function CostEstimate({ builder }: { builder: Builder }) {
  const t = useT();
  const { api } = useBff();
  const tenant = useMe().tenant_id;
  const text = useSettled(builder.problems.length ? null : builder.text, 600);
  const estimate = useQuery({
    queryKey: ["prices:estimate", tenant, text],
    queryFn: () => {
      const parsed = SpecDocument.parse(text ?? "");
      return data(api.POST("/v1/prices:estimate", { body: { agent: (parsed.ok ? parsed.doc.toJSON() : {}) as Record<string, unknown>, direction: "inbound" } }));
    },
    enabled: text !== null,
    placeholderData: keepPreviousData,
    staleTime: Infinity,
    retry: false,
  });
  if (estimate.isError) return <p className="text-sm text-[var(--color-muted)]">{t("builder.costUnknown")}</p>;
  const result = estimate.data;
  if (!result) return <p className="text-sm text-[var(--color-muted)]">{t("app.loading")}</p>;
  const unpriced = result.items.filter((i) => !i.priced).map((i) => (i.model ? `${i.provider} ${i.model}` : i.provider));
  return (
    <div className="space-y-1" aria-live="polite">
      <p className="text-lg font-semibold">{t("builder.costPerMinute", { amount: usd(result.per_minute) })}</p>
      {unpriced.length > 0 && <p className="text-sm text-[var(--color-warning)]">{t("builder.costUnpriced", { list: [...new Set(unpriced)].join(", ") })}</p>}
      <p className="text-xs text-[var(--color-muted)]">
        {t("builder.costAssumptions", {
          stt: result.assumptions.stt_seconds ?? 60, input: result.assumptions.llm_input_tokens ?? 0,
          output: result.assumptions.llm_output_tokens ?? 0, characters: result.assumptions.tts_characters ?? 0,
        })}
      </p>
    </div>
  );
}
